import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { AppBar } from "@/elements/components/ui/app-bar";
import { Id } from "@teachloop/backend/_generated/dataModel";
import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import React, { Fragment, useCallback, useState } from "react";
import { FlatList, Pressable, View } from "react-native";
import ChatItemProvider from "@/elements/components/chat/chat-item-provider";
import ChatBubble from "@/elements/components/chat/chat-bubble";
import Loader from "@/elements/components/shared/loader";
import { Input } from "@/elements/components/ui/input";
import { useChat } from "@/elements/components/chat/use-chat";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import SendMessageIcon from "@/assets/svgs/send-message-icon";
import AttachmentIcon from "@/assets/svgs/attachment-icon";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "../utils/convex-api";
import { Avatar } from "../ui/avatar";
import goBack from "../utils/go-back";

function TutorChat() {
  const router = useRouter();
  const { chatId } = useLocalSearchParams<{
    chatId: Id<"chats">;
  }>();
  const [message, setMessage] = useState("");
  const responsiveWidth = useResponsiveWidth();
  const {
    currentUser,
    results,
    isLoading,
    isPending,
    inputRef,
    handleSendMessage,
    renderMessage,
    handleLoadMore,
    pickFile,
    isUploading,
  } = useChat({ chatId, userType: "tutor" });
  const { data: chatDetails } = useQuery(
    convexQuery(
      api.tutor.inbox.queries.getChatDetails,
      chatId ? { chatId } : "skip"
    )
  );
  const prefix = chatDetails?.prefix ?? "";
  const imageUrl = chatDetails?.imageUrl ?? "";
  const parentName = chatDetails?.parentName ?? "";

  const renderMessageItem = useCallback(
    ({ item }: { item: any }) => {
      const chatItem = renderMessage({ item });
      return (
        <ChatItemProvider chatItem={chatItem}>
          <ChatBubble />
        </ChatItemProvider>
      );
    },
    [renderMessage]
  );

  const renderSeparator = useCallback(() => <View className="h-6" />, []);
  const renderFooter = useCallback(() => <View className="h-6" />, []);
  const renderHeader = useCallback(() => <View className="h-6" />, []);
  const renderEmpty = useCallback(
    () => (isLoading ? <Loader /> : <Fragment />),
    [isLoading]
  );

  if (!currentUser) {
    return <Loader />;
  }

  if (!chatId) {
    return <Redirect href={"/"} />;
  }

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <AppBar>
        <AppBar.Icon onPress={goBack} />
        <View className="flex-1 items-center justify-center flex-row gap-2">
          <Avatar size="sm">
            <Avatar.Content
              source={imageUrl ? { uri: imageUrl } : undefined}
              label={parentName}
              labelSize="sm"
              size="sm"
            />
          </Avatar>
          <AppBar.Title>{prefix}</AppBar.Title>
        </View>
        <View pointerEvents="none" className="opacity-0">
          <AppBar.Icon />
        </View>
      </AppBar>

      <FlatList
        inverted
        data={results}
        renderItem={renderMessageItem}
        ItemSeparatorComponent={renderSeparator}
        ListFooterComponent={renderFooter}
        ListHeaderComponent={renderHeader}
        onEndReached={handleLoadMore}
        ListEmptyComponent={renderEmpty}
        contentContainerStyle={
          results.length === 0
            ? { justifyContent: "center", flex: 1 }
            : {
                width: responsiveWidth,
                alignSelf: "center",
              }
        }
        removeClippedSubviews
        maxToRenderPerBatch={10}
        windowSize={10}
        keyExtractor={(item) => item._id}
      />

      <ResponsiveContent>
        <View className="mx-6 mb-6">
          <View>
            <Input
              ref={inputRef}
              disabled={isPending}
              placeholder="Type a message.."
              value={message}
              onChangeText={setMessage}
              onSubmitEditing={async ({ nativeEvent: { text } }) => {
                await handleSendMessage(text);
                setMessage("");
              }}
              className="pr-12"
            />
            <Pressable
              onPress={async () => {
                if (!!message.trim()) {
                  await handleSendMessage(message);
                } else {
                  await pickFile();
                }
                setMessage("");
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 disabled:opacity-50"
              disabled={isPending || isUploading}
            >
              {!message.trim() ? (
                <AttachmentIcon
                  className="size-6"
                  opacity={isPending || isUploading ? 0.5 : 1}
                />
              ) : (
                <SendMessageIcon
                  className="size-6"
                  opacity={isPending || isUploading ? 0.5 : 1}
                />
              )}
            </Pressable>
          </View>
        </View>
      </ResponsiveContent>
    </View>
  );
}

export default TutorChat;
